import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { store } from './index';
import { checkAuthStatus } from './slices/authSlice';
import { fetchMySubscription } from './slices/subscriptionSlice';
import { fetchAIProviders, fetchVoiceProviders, fetchCurrentConfig } from './slices/providersSlice';
import { fetchIntegrations, fetchMCPServers } from './slices/integrationsSlice';
import { useAppDispatch, useAppSelector } from './hooks';
import tokenManager from '../utils/tokenManager';
import tokenStorage from '../utils/tokenStorage';

// Component to handle initial data loading
const DataLoader = ({ children }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  useEffect(() => {
    console.log('🔍 [ReduxProvider] Checking for tokens on app load...');

    // Check auth if we have either access or refresh tokens (persisted)
    const accessToken = tokenStorage.getAccessToken();
    const refreshToken = tokenStorage.getRefreshToken();
    const hasCookieToken =
      document.cookie.includes('accessToken=') || document.cookie.includes('refreshToken=');

    console.log('🔍 [ReduxProvider] Token status:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasCookieToken,
      cookies: document.cookie,
      accessTokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : 'NONE',
      refreshTokenPreview: refreshToken ? `${refreshToken.substring(0, 20)}...` : 'NONE',
    });

    // Debug localStorage contents
    console.log('🔍 [ReduxProvider] localStorage contents:', {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
      tokenExpiry: localStorage.getItem('tokenExpiry'),
      allKeys: Object.keys(localStorage),
    });

    // Helper to validate token format (basic JWT check)
    const isValidTokenFormat = token => {
      if (!token) return false;
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        // Try to decode the payload
        JSON.parse(atob(parts[1]));
        return true;
      } catch {
        return false;
      }
    };

    // Only call auth APIs if we have at least one valid token
    // Check both localStorage and cookies, and validate token format
    const hasValidAccessToken =
      (accessToken && isValidTokenFormat(accessToken)) || document.cookie.includes('accessToken=');
    const hasValidRefreshToken =
      (refreshToken && isValidTokenFormat(refreshToken)) ||
      document.cookie.includes('refreshToken=');
    const hasAnyValidToken = hasValidAccessToken || hasValidRefreshToken;

    if (hasAnyValidToken) {
      console.log('🔍 [ReduxProvider] Valid tokens found, dispatching checkAuthStatus...');
      dispatch(checkAuthStatus());
    } else {
      console.log(
        '🔍 [ReduxProvider] No valid tokens found in localStorage or cookies, skipping auth check'
      );
    }
  }, [dispatch]);

  useEffect(() => {
    // Only load data if not on login/register pages
    const currentPath = window.location.pathname;
    const isPublic = currentPath === '/login' || currentPath === '/register' || currentPath === '/';

    if (!isPublic) {
      // Load public data for dashboard pages
      const loadPublicData = async () => {
        await Promise.all([
          dispatch(fetchAIProviders()),
          dispatch(fetchVoiceProviders()),
          dispatch(fetchCurrentConfig()),
          dispatch(fetchIntegrations()),
          dispatch(fetchMCPServers()),
        ]);
      };

      loadPublicData();
    }
  }, [dispatch]);

  useEffect(() => {
    console.log('🔍 [ReduxProvider] Auth state changed:', { isAuthenticated });

    // Load protected data only if authenticated
    if (isAuthenticated) {
      console.log('🔍 [ReduxProvider] Starting token manager...');
      // Fetch subscription when authenticated
      dispatch(fetchMySubscription()).catch(err => {
        console.warn('🔍 [ReduxProvider] Failed to fetch subscription:', err);
      });
      // Start automatic token refresh with a small delay to ensure auth state is fully initialized
      setTimeout(() => {
        console.log('🔍 [ReduxProvider] Starting token manager after delay...');
        tokenManager.startTokenRefresh();
      }, 1000);
      // WebSocket connection will be initialized by components that need it (e.g., Testing component)
      // This avoids unnecessary connections on login/public pages
    } else {
      console.log('🔍 [ReduxProvider] Stopping token manager...');
      // Stop token refresh if not authenticated
      tokenManager.stopTokenRefresh();
    }
  }, [isAuthenticated, dispatch]);

  return children;
};

// Main Redux Provider component
export const ReduxProvider = ({ children }) => {
  return (
    <Provider store={store}>
      <DataLoader>{children}</DataLoader>
    </Provider>
  );
};

export default ReduxProvider;
